import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import DataGrid, {
  Column,
  Paging,
  Scrolling,
  SearchPanel,
  FilterRow,
  HeaderFilter,
} from "devextreme-react/data-grid";
import { useSnackbar } from "notistack";
import envConfig from "../../config";
import { getCookie, getHeaders, default_headers } from "../../utils/api";
import CustomTabs from "../../components/Tabs/Tabs";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useApiCall from "../../hooks/ApiCall";
import BackdropLoading from "../../components/BackdropLoading/BackdropLoading";
import ManageSubscriberBusinessFileTab from "./ManageSubscriberBusinessFileTab";
import LinkSubscriberToATO from "./LinkToATO";
import SubscriberDetails from "./SubscriberDetails";
import PaymentMethodSection from "./PaymentMethodSection";
import InviteSubscriberPage from "../InviteSubscriberPage/InviteSubscriberDialog";
import { useTheme } from "../../context/ThemeContext";
import { getAppTheme } from "../../config/resolve-app-brand";

const MobileDataCell = ({ data }) => {
  return (
    <Box className="p-2">
      <Typography className="font-medium">ID: {data.xxrUser_xxuID}</Typography>
      <Typography>Email: {data.xxrLoginEmail}</Typography>
      <Typography>
        Name: {data.xxrFirstName} {data.xxrLastName}
      </Typography>
      <Typography>
        Status: {data.xxrStatus === "A" ? "Active" : "Inactive"}
      </Typography>
      <Typography>
        Add BF: {data.xxrCanAddBFs === "Y" ? "Yes" : "No"}
      </Typography>
      <Typography>
        Lodge STP: {data.xxrCanLodgeSTP === "Y" ? "Yes" : "No"}
      </Typography>
    </Box>
  );
};

const ManageSubscriber = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTab, setSelectedTab] = useState(0);
  const [membersData, setMembersData] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [fullSubscriberDetails, setFullSubscriberDetails] = useState({});
  const [open, setOpen] = useState(false);

  const [menuPosition, setMenuPosition] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [removeAccessDialogOpen, setRemoveAccessDialogOpen] = useState(false);
  const [memberToRemoveAccess, setMemberToRemoveAccess] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const actionsColumn = {
    caption: "Actions",
    width: 90,
    allowFiltering: false,
    allowSorting: false,
    alignment: "center",
    cellRender: ({ data }) => (
      <IconButton
        onClick={(event) => handleActionMenuOpen(event, data)}
        data-testid="manage-subscriber--IconButton-0"
      >
        <MoreVertIcon />
      </IconButton>
    ),
  };

  const {
    data: subscribersData,
    error: subscribersError,
    loading: subscribersLoading,
  } = useApiCall({
    url: `https://${
      envConfig.apiDev1
    }/api/v3/en-au/subscribers/get-selection-list?LoginEmail=${getCookie(
      "loginEmail",
    )}&BaseHostURL=${envConfig.loginUrl}`,
    method: "GET",
    headers: {},
    navigate,
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleActionMenuOpen = (event, member) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom,
      left: rect.left,
    });
    setSelectedMember(member);
  };

  const handleActionMenuClose = () => {
    setMenuPosition(null);
    setSelectedMember(null);
  };

  const fetchMembers = useCallback(async () => {
    if (!selectedSubscriber?.xxsId) return;
    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/subscribers/get-list-of-subscribers-members?subscriberId=${selectedSubscriber?.xxsId}&BaseHostURL=${envConfig.loginUrl}`,
        {
          method: "GET",
          headers: getHeaders(),
        },
      );

      const data = await response.json();
      if (data.SuccessYN === "Y") {
        setMembersData(data.ListOfMembers || []);
      }
    } catch (error) {
      // Error handled by interceptor
    }
  }, [enqueueSnackbar, selectedSubscriber?.xxsId]);

  const fetchSubscriberDetails = useCallback(
    async (subscriberIdOverride) => {
      const subscriberId =
        subscriberIdOverride ?? selectedSubscriber?.xxsId ?? id;
      if (!subscriberId) return;
      setLoadingDetails(true);
      try {
        const response = await fetch(
          `https://${envConfig.apiDev1}/api/v3/en-au/subscribers/get-subscriber-details?subscriberId=${subscriberId}&BaseHostURL=${envConfig.loginUrl}`,
          {
            method: "GET",
            headers: getHeaders(),
          },
        );

        const data = await response.json();
        if (data.SuccessYN === "Y") {
          setFullSubscriberDetails(data.SubscriberDetails || {});
        }
        setLoadingDetails(false);
      } catch (error) {
        setLoadingDetails(false);
      }
    },
    [selectedSubscriber?.xxsId, id],
  );

  useEffect(() => {
    fetchSubscriberDetails();
  }, [fetchSubscriberDetails, selectedSubscriber]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    const setupId = searchParams.get("search");
    if (!setupId || !id) return;

    const controller = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://${envConfig.apiDev1Exacc}/api/v1/en-au/stripe-exacc/get-setup-status-on-success-async?BaseHostURL=${envConfig.mainServiceUrl}`,
          {
            method: "POST",
            headers: {
              ...default_headers,
              "api-key": "OlivsAuth",
            },
            body: JSON.stringify({
              Id: setupId,
              DatabaseId: 24960,
            }),
            signal: controller.signal,
          },
        );

        const data = await response.json();

        if (data.SuccessYN === "Y") {
          enqueueSnackbar("Payment setup completed successfully.", {
            variant: "success",
          });
          await fetchSubscriberDetails(id);
        }
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
        // Error handled by interceptor
      } finally {
        setLoading(false);
        if (!controller.signal.aborted) {
          navigate(`/subscriber/${id}`, { replace: true });
        }
      }
    })();

    return () => controller.abort();
  }, [searchParams, id, navigate, enqueueSnackbar, fetchSubscriberDetails]);

  const handleActivateDeactivate = async (newStatus) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/subscribers/change-subscriber-membership-status?BaseHostURL=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getHeaders(),
          },
          body: JSON.stringify({
            SubscriberID: selectedSubscriber?.xxsId,
            SubscriberUserID: selectedMember?.xxrUser_xxuID,
            ActiveYN: newStatus,
          }),
        },
      );

      const data = await response.json();

      if (data.SuccessYN === "Y") {
        enqueueSnackbar("Status updated successfully", {
          variant: "success",
        });
        await fetchMembers();
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
    handleActionMenuClose();
  };

  const handleLodgeSTPToggle = async (newLodge, canAddBF) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/subscribers/change-subscriber-membership-settings?BaseHostURL=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getHeaders(),
          },
          body: JSON.stringify({
            SubscriberID: selectedSubscriber?.xxsId,
            SubscriberUserID: selectedMember?.xxrUser_xxuID,
            CanAddBusinessFilesYN: canAddBF,
            CanLodgeSTPYN: newLodge,
          }),
        },
      );

      const data = await response.json();

      if (data.SuccessYN === "Y") {
        enqueueSnackbar("Status updated successfully", {
          variant: "success",
        });
        await fetchMembers();
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
    handleActionMenuClose();
  };

  const handleResendInvitation = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/subscribers/send-invitation-email-to-subscriber?BaseHostURL=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getHeaders(),
          },
          body: JSON.stringify({
            SubscriberID: selectedSubscriber?.xxsId,
            SubscriberUserID: selectedMember?.xxrUser_xxuID,
          }),
        },
      );

      const data = await response.json();

      if (data.SuccessYN === "Y") {
        enqueueSnackbar("Invitation email sent.", {
          variant: "success",
        });
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
    handleActionMenuClose();
  };

  useEffect(() => {
    console.log(id);
    console.log(subscribersData?.ListOfSubscribers?.length > 0);
    if (
      id &&
      subscribersData?.ListOfSubscribers &&
      subscribersData.ListOfSubscribers.length > 0
    ) {
      console.log(subscribersData.ListOfSubscribers);
      const subscriberFromUrl = subscribersData.ListOfSubscribers.find(
        (subscriber) => subscriber.xxsId == id,
      );
      console.log(subscriberFromUrl);
      if (subscriberFromUrl) {
        setSelectedSubscriber(subscriberFromUrl);
      }
    }
  }, [id, subscribersData]);

  // Define the search expression to limit search to specific fields
  const searchExpr = ["xxrUser_xxuID", "xxrFirstName", "xxrLastName"];

  const mobileDetailsColumn = {
    caption: "Details",
    allowFiltering: false,
    allowSorting: false,
    cellRender: ({ data }) => <MobileDataCell data={data} />,
  };

  const handleClearDevRego = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${envConfig.apiDev2}/api/security/set-test-environment-for-new-user-registration-v2`,
        {
          method: "GET",
          headers: getHeaders(),
        },
      );

      const data = await response.json();
      if (data.SuccessYN === "Y") {
        enqueueSnackbar("Dev registration cleared successfully", {
          variant: "success",
        });
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const openStripePaymentSetup = async (mode) => {
    if (!selectedSubscriber?.xxsId) return;

    setLoading(true);
    try {
      const loginPortalOrigin = envConfig.loginPortalUrl
        ? `https://${envConfig.loginPortalUrl}`
        : window.location.origin;
      const successUrl = `${loginPortalOrigin}/subscriber/${selectedSubscriber.xxsId}?search=`;

      const path =
        mode === "create"
          ? "stripe-exacc/create-payment-setup-for-charges-async"
          : "stripe-exacc/edit-payment-setup-for-charges-async";

      const body =
        mode === "create"
          ? {
              SubscriberId: selectedSubscriber.xxsId,
              OnExistRecreateExaccUserYN: "Y",
              SuccessURL: successUrl,
            }
          : {
              SubscriberId: selectedSubscriber.xxsId,
              SuccessURL: successUrl,
            };

      const response = await fetch(
        `https://${envConfig.apiDev1Exacc}/api/v1/en-au/${path}?BaseHostURL=${envConfig.mainServiceUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getHeaders(),
          },
          body: JSON.stringify(body),
        },
      );

      const data = await response.json();

      const paymentSetupUrl =
        data.Data?.PaymentSetupUrl ?? data.PaymentSetupUrl;

      if (paymentSetupUrl) {
        window.location.assign(paymentSetupUrl);
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleClearTesterVerification = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${envConfig.apiDev2}/api/security/set-test-environment-for-new-user-verification-v2`,
        {
          method: "GET",
          headers: getHeaders(),
        },
      );

      const data = await response.json();
      if (data.SuccessYN === "Y") {
        enqueueSnackbar("Tester verification cleared successfully", {
          variant: "success",
        });
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAllAccess = async () => {
    if (!memberToRemoveAccess) return;

    setLoading(true);
    setRemoveAccessDialogOpen(false);

    try {
      const response = await fetch(
        `https://${envConfig.apiDev1Exacc}/api/v1/en-au/database/remove-all-databases-access-async?BaseHostURL=${envConfig.mainServiceUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getHeaders(),
          },
          body: JSON.stringify({
            SubscriberId: selectedSubscriber?.xxsId,
            TargetGlobalUserId: memberToRemoveAccess?.xxrUser_xxuID,
            RemoveAccessYN: "N",
            DeactiveAccessYN: "Y",
          }),
        },
      );

      const data = await response.json();

      if (data.SuccessYN === "Y") {
        const filesCount = data.ListOfDatabases?.length || 0;
        const actionText =
          data.DeactivedAccessYN === "Y" ? "deactivated" : "removed";

        enqueueSnackbar(
          `Access ${actionText} successfully from ${filesCount} business file(s)`,
          { variant: "success" },
        );

        await fetchMembers(); // Refresh the members list
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
      setMemberToRemoveAccess(null);
    }
  };

  const desktopColumns = [
    { dataField: "xxrUser_xxuID", caption: "User ID" },
    { dataField: "xxrLoginEmail", caption: "Email" },
    { dataField: "xxrFirstName", caption: "First Name" },
    { dataField: "xxrLastName", caption: "Last Name" },
    {
      dataField: "xxrStatus",
      caption: "Status",
      calculateDisplayValue: (rowData) =>
        rowData.xxrStatus === "A" ? "Active" : "Inactive",
    },
    {
      dataField: "xxrCanAddBFs",
      caption: "Add BF",
      calculateDisplayValue: (rowData) =>
        rowData.xxrCanAddBFs === "Y" ? "Yes" : "No",
    },
    {
      dataField: "xxrCanLodgeSTP",
      caption: "Lodge STP",
      calculateDisplayValue: (rowData) =>
        rowData.xxrCanLodgeSTP === "Y" ? "Yes" : "No",
    },
  ];

  return (
    <Box p={2}>
      <InviteSubscriberPage open={open} onClose={() => setOpen(false)} />
      <BackdropLoading open={loading || subscribersLoading} />
      <Typography
        onClick={() => navigate("/create-new-subscriber")}
        variant="h4"
        gutterBottom
        style={{ cursor: "pointer" }}
        data-testid="manage-subscriber--Typography-0"
      >
        Subscriber Account
      </Typography>
      <Autocomplete
        sx={{
          mt: 3,
          maxWidth: "480px",
        }}
        autoSelect={true}
        autoHighlight={true}
        options={subscribersData?.ListOfSubscribers || []}
        getOptionLabel={(option) => option?.xxsName || ""}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Subscriber Account"
            variant="outlined"
            fullWidth
            data-testid="manage-subscriber--TextField-0"
          />
        )}
        value={selectedSubscriber}
        onChange={(_, value) => {
          setSelectedSubscriber(value);
          if (value && value.xxsId) {
            navigate(`/subscriber/${value.xxsId}`);
          } else {
            navigate("/subscriber");
          }
        }}
        data-testid="manage-subscriber--Autocomplete-0"
      />
      {selectedSubscriber?.xxsId && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 3,
            mt: 2,
          }}
        >
          <CustomTabs
            orientation="vertical"
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Content of Selected Tab */}
            {selectedTab === 0 && (
              <Box
                p={4}
                sx={{
                  boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
                  border: theme.palette.borders.main,
                  borderRadius: 1,
                }}
              >
                <PaymentMethodSection
                  subscriberDetails={fullSubscriberDetails}
                  loading={loadingDetails}
                  stripeLoading={loading}
                  onSetupPayment={() => openStripePaymentSetup("create")}
                  onUpdatePayment={() => openStripePaymentSetup("edit")}
                />
                <SubscriberDetails
                  selectedSubscriber={selectedSubscriber}
                  fullSubscriberDetails={fullSubscriberDetails}
                  loading={loadingDetails}
                  fetchSubscriberDetails={fetchSubscriberDetails}
                />
              </Box>
            )}
            {selectedTab === 1 && (
              <Box
                p={4}
                sx={{
                  boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
                  border: theme.palette.borders.main,
                  borderRadius: 1,
                }}
              >
                <>
                  <Box
                    sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        // setOpen(true);
                        navigate(`/add-member-to-subscriber/${id}`);
                      }}
                      data-testid="manage-subscriber--Button-0"
                    >
                      + New Member
                    </Button>
                    {selectedSubscriber?.xxsId === 20354 ||
                      (selectedSubscriber?.xxsId === 10177 && (
                        <>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleClearDevRego}
                            data-testid="manage-subscriber--Button-1"
                          >
                            Clear Dev Rego
                          </Button>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleClearTesterVerification}
                            data-testid="manage-subscriber--Button-2"
                          >
                            Clear Tester Verification
                          </Button>
                        </>
                      ))}
                  </Box>

                  <DataGrid
                    dataSource={membersData}
                    showBorders={true}
                    wordWrapEnabled={true}
                    keyExpr="xxrUser_xxuID"
                    searchExpr={searchExpr}
                    // Enable sorting on all columns by default
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                  >
                    <Scrolling mode="virtual" />
                    <Paging defaultPageSize={10} />
                    <SearchPanel
                      visible={true}
                      width={240}
                      placeholder="Search by ID, First Name, Last Name"
                    />
                    {/* Enable Filter Row */}
                    <HeaderFilter visible={true} />
                    <Column {...actionsColumn} />
                    {isMobile ? (
                      <Column {...mobileDetailsColumn} />
                    ) : (
                      desktopColumns.map((column, index) => (
                        <Column key={column.dataField || index} {...column} />
                      ))
                    )}
                    {/* <Column
                  dataField="xxlIsAdmin"
                  caption="Type"
                  calculateDisplayValue={(rowData) =>
                    rowData.xxlIsAdmin === "Y" ? "Admin" : "User"
                  }
                /> */}
                  </DataGrid>
                </>
              </Box>
            )}
            {selectedSubscriber?.xxsId &&
              selectedMember &&
              selectedTab === 1 && (
                <Menu
                  open={Boolean(menuPosition)}
                  onClose={handleActionMenuClose}
                  anchorEl={menuPosition ? document.body : null} // anchorEl to bind to the element
                  anchorReference="anchorPosition"
                  anchorPosition={
                    menuPosition
                      ? { top: menuPosition.top, left: menuPosition.left }
                      : undefined
                  }
                >
                  <MenuItem
                    onClick={() =>
                      handleActivateDeactivate(
                        selectedMember?.xxrStatus === "A" ? "N" : "Y",
                      )
                    }
                    data-testid="manage-subscriber--MenuItem-0"
                  >
                    {selectedMember?.xxrStatus === "A"
                      ? "Deactivate"
                      : "Activate"}
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleLodgeSTPToggle(
                        selectedMember?.xxrCanLodgeSTP,
                        selectedMember?.xxrCanAddBFs === "Y" ? "N" : "Y",
                      )
                    }
                    data-testid="manage-subscriber--MenuItem-1"
                  >
                    {selectedMember?.xxrCanAddBFs === "Y"
                      ? "Disable Adding Business Files"
                      : "Enable Adding Business Files"}
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleLodgeSTPToggle(
                        selectedMember?.xxrCanLodgeSTP === "Y" ? "N" : "Y",
                        selectedMember?.xxrCanAddBFs,
                      )
                    }
                    data-testid="manage-subscriber--MenuItem-2"
                  >
                    {selectedMember?.xxrCanLodgeSTP === "Y"
                      ? "Disable Lodging STP as Tax Agent"
                      : "Enable Lodging STP as Tax Agent"}
                  </MenuItem>
                  {selectedMember?.xxrStatus !== "A" && (
                    <MenuItem
                      onClick={handleResendInvitation}
                      data-testid="manage-subscriber--MenuItem-resend-invitation"
                    >
                      Re-send invitation
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      setMemberToRemoveAccess(selectedMember);
                      setRemoveAccessDialogOpen(true);
                      handleActionMenuClose();
                    }}
                    data-testid="manage-subscriber--MenuItem-3"
                  >
                    Remove all access
                  </MenuItem>
                </Menu>
              )}
            {/* Other Tabs Content */}
            {selectedSubscriber?.xxsId && selectedTab === 2 && (
              <Box
                p={4}
                sx={{
                  boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
                  border: theme.palette.borders.main,
                  borderRadius: 1,
                }}
              >
                <ManageSubscriberBusinessFileTab
                  selectedSubscriber={selectedSubscriber}
                />
              </Box>
            )}
            {selectedSubscriber?.xxsId && selectedTab === 3 && (
              <Box
                p={4}
                sx={{
                  boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
                  border: theme.palette.borders.main,
                  borderRadius: 1,
                }}
              >
                <LinkSubscriberToATO
                  selectedSubscriber={selectedSubscriber}
                  subscriberDetails={fullSubscriberDetails}
                  loading={loadingDetails}
                  fetchSubscriberDetails={fetchSubscriberDetails}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* Remove All Access Dialog */}
      <Dialog
        open={removeAccessDialogOpen}
        onClose={() => {
          setRemoveAccessDialogOpen(false);
          setMemberToRemoveAccess(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
          },
        }}
        data-testid="manage-subscriber--Dialog-0"
      >
        <DialogTitle
          sx={{ fontWeight: "bold" }}
          data-testid="manage-subscriber--DialogTitle-0"
        >
          {memberToRemoveAccess?.xxrFirstName}{" "}
          {memberToRemoveAccess?.xxrLastName} - remove all access
        </DialogTitle>
        <DialogContent data-testid="manage-subscriber--DialogContent-0">
          <DialogContentText sx={{ color: "text.primary" }}>
            Do you want to remove all access to Subscribers and Business Files,
            where you are Admin or Owner?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: "text.primary" }}>
            Access will be removed wherever {memberToRemoveAccess?.xxrFirstName}{" "}
            {memberToRemoveAccess?.xxrLastName} is not an Owner, whether access
            was granted through Subscriber account(s) or directly. We will
            search for all files we can remove{" "}
            {memberToRemoveAccess?.xxrFirstName} from.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ p: 2 }}
          data-testid="manage-subscriber--DialogActions-0"
        >
          <Button
            onClick={() => {
              setRemoveAccessDialogOpen(false);
              setMemberToRemoveAccess(null);
            }}
            color="primary"
            data-testid="manage-subscriber--Button-3"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveAllAccess}
            variant="contained"
            color="primary"
            data-testid="manage-subscriber--Button-4"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageSubscriber;
